/**
 * Greek language helpers shared by the API (what is sent) and the web app (what is shown), so a
 * greeting reads the same in an SMS, an email, the review page and the dashboard.
 */

const VOWELS = /[αεηιουωάέήίόύώϊϋΐΰ]/;
const STRESSED = /[άέήίόύώΐΰ]/;
const GREEK_RE = /[Ͱ-Ͽἀ-῿]/;

/** Names whose usual vocative does not follow the stress rule below. */
const VOCATIVE_EXCEPTIONS: Record<string, string> = {
  Στέλιος: 'Στέλιο', // -ιος pronounced as one syllable (synizesis)
  Παύλος: 'Παύλε',
  Πέτρος: 'Πέτρο',
};

/** 1-based index (from the end) of the vowel group carrying the stress, or -1. */
function stressedGroupFromEnd(word: string): number {
  const lower = word.toLocaleLowerCase('el-GR');
  let groups = 0;
  let inGroup = false;
  for (let i = lower.length - 1; i >= 0; i--) {
    const ch = lower[i]!;
    if (VOWELS.test(ch)) {
      if (!inGroup) {
        groups++;
        inGroup = true;
      }
      if (STRESSED.test(ch)) return groups;
    } else inGroup = false;
  }
  return -1;
}

/**
 * Greek vocative of a first name, for greetings ("Γεια σας Νίκο"). Covers the common masculine
 * endings; feminine and non-Greek names are returned unchanged.
 *   -ης → -η (Θανάσης → Θανάση), -ας → -α (Κώστας → Κώστα, Λουκάς → Λουκά),
 *   -ος → -ο when stressed on the penult (Νίκος → Νίκο, Γιώργος → Γιώργο),
 *   -ος → -ε when stressed earlier (Αλέξανδρος → Αλέξανδρε, Μάριος → Μάριε, Δημήτριος → Δημήτριε),
 *   -ός → -έ (rare as first names).
 */
export function greekVocative(name: string): string {
  const n = name.trim();
  if (!GREEK_RE.test(n)) return n;
  const exception = VOCATIVE_EXCEPTIONS[n];
  if (exception) return exception;
  if (/ής$/.test(n)) return n.slice(0, -2) + 'ή';
  if (/ης$/.test(n)) return n.slice(0, -1);
  if (/[αά]ς$/.test(n)) return n.slice(0, -1);
  if (/ός$/.test(n)) return n.slice(0, -2) + 'έ';
  if (/ος$/.test(n)) {
    // In "-ιος" the final ο is its own syllable (Μά-ρι-ος, Βα-σί-λει-ος).
    const stress = /ιος$/.test(n)
      ? stressedGroupFromEnd(n.slice(0, -2)) + 1
      : stressedGroupFromEnd(n);
    return stress >= 3 ? n.slice(0, -2) + 'ε' : n.slice(0, -1);
  }
  return n;
}

/**
 * The first name as used when addressing the recipient (`{{firstName}}` in a message, the
 * greeting on the review page): the vocative in Greek, unchanged otherwise.
 */
export function addressFirstName(
  name: string | null | undefined,
  locale: 'el' | 'en' | string,
): string | null | undefined {
  if (name == null) return name;
  return locale === 'el' ? greekVocative(name) : name;
}

const ALREADY_QUOTED = /[«»"“”„]/;

/**
 * A business name in quotation marks for running text — «…» in Greek, “…” in English — unless it
 * already contains guillemets or quotes ("Κουρείο «Ο Αλέξης»"), which would otherwise nest as
 * «Κουρείο «Ο Αλέξης»». i18n strings take the pre-quoted value and carry no quotes themselves.
 */
export function quoteName(name: string, locale: 'el' | 'en' | string = 'el'): string {
  const n = name.trim();
  if (!n) return n;
  if (ALREADY_QUOTED.test(n)) return n;
  return locale === 'en' ? `“${n}”` : `«${n}»`;
}
